import apiClient from "./api";
import { getApiAccessToken } from "./UserService";

export interface GeneralSidecostItem {
  id: number;
  itemNumber: number;
  itemSubnumber: number | null;
  itemCode?: string | null;
  name: string;
  description: string;
  unitName: string;
  orderInGroup: number;
  includeInReport: boolean;
  requireAdditionalInfo: boolean;
  referenceCodes: string;
  invoicingReferenceCode: string;
  sidecostItemType: number;
  isInUse: boolean;
}

export interface GeneralSidecostGroup {
  id: number;
  name: string;
  description: string;
  orderInGroup: number;
  includeInReport: boolean;
  isInUse: boolean;
  items: GeneralSidecostItem[];
}

export interface GeneralSidecostGroupsResponse {
  hideWorkItemNumber: boolean;
  useWorkItemCode: boolean;
  groups: GeneralSidecostGroup[];
}

export interface GeneralSidecostOrderOption {
  value: number;
  label: string;
}

export interface GeneralSidecostGroupOption {
  id: number;
  name: string;
  orderInGroup: number;
}

export interface GeneralSidecostFormMetadata {
  groupOrderOptions: GeneralSidecostOrderOption[];
  groupOptions: GeneralSidecostGroupOption[];
  unitOptions: string[];
}

export interface UpsertGeneralSidecostGroupRequest {
  name: string;
  description?: string;
  orderInGroup: number;
  includeInReport: boolean;
  currentOrderInGroup?: number | null;
}

export interface UpsertGeneralSidecostItemRequest {
  sidecostGroupId: number;
  name: string;
  description?: string;
  orderInGroup: number;
  itemNumber: number;
  itemSubnumber: number | null;
  itemCode?: string | null;
  unitName: string;
  includeInReport: boolean;
  requireAdditionalInfo: boolean;
  referenceCodes?: string;
  invoicingReferenceCode?: string;
  sidecostItemType: number;
  currentOrderInGroup?: number | null;
}

async function authHeader(): Promise<Record<string, string>> {
  const token = await getApiAccessToken();
  return { Authorization: `Bearer ${token}` };
}

export const getGeneralSidecostGroups = async (
  addressSystemId: number,
): Promise<GeneralSidecostGroupsResponse> => {
  const headers = await authHeader();
  const response = await apiClient.get<GeneralSidecostGroupsResponse>(
    `/api/GeneralSidecostGroups/${addressSystemId}`,
    { headers },
  );
  return response.data;
};

export const getGeneralSidecostFormMetadata = async (
  addressSystemId: number,
  groupId?: number,
): Promise<GeneralSidecostFormMetadata> => {
  const headers = await authHeader();
  const response = await apiClient.get<GeneralSidecostFormMetadata>(
    `/api/GeneralSidecostGroups/${addressSystemId}/metadata`,
    { headers, params: groupId ? { groupId } : undefined },
  );
  return response.data;
};

export const createGeneralSidecostGroup = async (
  addressSystemId: number,
  request: UpsertGeneralSidecostGroupRequest,
): Promise<number> => {
  const headers = await authHeader();
  const response = await apiClient.post<{ id: number }>(
    `/api/GeneralSidecostGroups/${addressSystemId}/groups`,
    request,
    { headers },
  );
  return response.data.id;
};

export const updateGeneralSidecostGroup = async (
  addressSystemId: number,
  groupId: number,
  request: UpsertGeneralSidecostGroupRequest,
): Promise<void> => {
  const headers = await authHeader();
  await apiClient.put(
    `/api/GeneralSidecostGroups/${addressSystemId}/groups/${groupId}`,
    request,
    { headers },
  );
};

export const deleteGeneralSidecostGroup = async (
  addressSystemId: number,
  groupId: number,
): Promise<void> => {
  const headers = await authHeader();
  await apiClient.delete(
    `/api/GeneralSidecostGroups/${addressSystemId}/groups/${groupId}`,
    { headers },
  );
};

export const createGeneralSidecostItem = async (
  addressSystemId: number,
  request: UpsertGeneralSidecostItemRequest,
): Promise<number> => {
  const headers = await authHeader();
  const response = await apiClient.post<{ id: number }>(
    `/api/GeneralSidecostGroups/${addressSystemId}/items`,
    request,
    { headers },
  );
  return response.data.id;
};

export const updateGeneralSidecostItem = async (
  addressSystemId: number,
  itemId: number,
  request: UpsertGeneralSidecostItemRequest,
): Promise<void> => {
  const headers = await authHeader();
  await apiClient.put(
    `/api/GeneralSidecostGroups/${addressSystemId}/items/${itemId}`,
    request,
    { headers },
  );
};

export const deleteGeneralSidecostItem = async (
  addressSystemId: number,
  itemId: number,
): Promise<void> => {
  const headers = await authHeader();
  await apiClient.delete(
    `/api/GeneralSidecostGroups/${addressSystemId}/items/${itemId}`,
    { headers },
  );
};

export const moveGeneralSidecostGroup = async (
  addressSystemId: number,
  groupId: number,
  direction: -1 | 1,
): Promise<void> => {
  const headers = await authHeader();
  await apiClient.post(
    `/api/GeneralSidecostGroups/${addressSystemId}/groups/${groupId}/move`,
    { direction },
    { headers },
  );
};

export const moveGeneralSidecostItem = async (
  addressSystemId: number,
  groupId: number,
  itemId: number,
  direction: -1 | 1,
): Promise<void> => {
  const headers = await authHeader();
  await apiClient.post(
    `/api/GeneralSidecostGroups/${addressSystemId}/groups/${groupId}/items/${itemId}/move`,
    { direction },
    { headers },
  );
};
